<template>
  <div :class="{ 'has-designs-rail': railVisible }">
    <v-app class="mcs">
      <PageLoadingProgress />
      <template v-if="!appLoading">
        <template v-if="authorized">
          <CollectionMultiAppBar />
          <AlertBar app />

          <Nav v-if="!iframed" />
          <Header v-if="!iframed" />

          <!-- Standalone AiDesigner "previous designs" rail. Self-positions:
               left side column on desktop, bottom bar on mobile. Loaded lazily
               via loadDesignsRailScript() once the user is authorized. It stays
               in the DOM (so it can fetch designs) but reports `rail-visibility`
               — we only reveal it / reserve the layout gutter once it has
               designs to show (see the `has-designs-rail` class + styles). -->
          <mcs-designs-rail
            v-if="!iframed && isMCS"
            id="designs-rail-component"
            @rail-visibility="onRailVisibility"
          ></mcs-designs-rail>
        </template>
        <template v-else>
          <UnauthorizedHeader v-if="!iframed" />
        </template>
        <v-main
          class="greyLight"
          :style="{ 'margin-top': selected.length ? '-64px' : 'inherit' }"
        >
          <PromoBanner v-if="authorized" />
          <v-container class="h-full pt-6 px-8">
            <router-view :key="routeKey" />
          </v-container>
          <Footer v-if="!iframed" />
          <Snackbar ref="snackbar" />
        </v-main>
        <mcs-checkout id="checkout-component"></mcs-checkout>
        <mcs-checkout-subscription id="checkout-subscription-component"></mcs-checkout-subscription>
        <template v-if="authorized">
          <SubscriptionCheckoutDrawer v-if="!iframed" />
          <ReactivateSubscriptionDrawer />
          <CancelSubscriptionDrawer />
          <DefaultPaymentMethodDrawer />
          <AddCardDrawer />
          <UpdateCardDrawer />
          <ReviewDrawer />
          <ProjectProofDrawer />
          <ZoomImageOverlay />
          <CatalogDrawer />
          <EditorDrawer />
          <NonProfitDrawer />
          <ExemptionCertificatesDrawer />
          <AddPeopleDrawer />
          <ForcePassword />
          <RenameDrawer />
          <BlankCanvasDrawer />
          <BlankCanvasPresetDrawer />
          <Project3dDrawer />
          <ShareToAccount />
        </template>
      </template>
    </v-app>
    <GenericAlert ref="alert" />
    <GenericConfirm ref="confirm" />
    <Clipboard ref="clipboard" />
  </div>
</template>

<script>
import CollectionMultiAppBar from '@/components/collections/admin/MultiAppBar';
import AddPeopleDrawer from '@/components/people/Add';
import ForcePassword from '@/components/profile/ForcePassword';
import Project3dDrawer from '@/components/projects/3d/Drawer';
import ShareToAccount from '@/components/projects/ShareToAccount';
import BlankCanvasDrawer from '@/components/projects/blankCanvas/Drawer';
import BlankCanvasPresetDrawer from '@/components/projects/blankCanvas/PresetDrawer';
import { mapGetters } from 'vuex';
import Clipboard from './components/alerts/Clipboard';
import GenericAlert from './components/alerts/GenericAlert';
import GenericConfirm from './components/alerts/GenericConfirm';
import Snackbar from './components/alerts/Snackbar';
import CatalogDrawer from './components/catalog/CatalogDrawer';
import EditorDrawer from './components/editor/EditorDrawer';
import AlertBar from './components/layout/AlertBar';
import Footer from './components/layout/Footer';
import Header from './components/layout/Header';
import Nav from './components/layout/Nav';
import UnauthorizedHeader from './components/layout/UnauthorizedHeader';
import AddCardDrawer from './components/payments/AddCardDrawer';
import UpdateCardDrawer from './components/payments/UpdateCardDrawer';
import ProjectProofDrawer from './components/print/ProjectProofDrawer';
import ExemptionCertificatesDrawer from './components/profile/nonprofit/ExemptionCertificatesDrawer';
import NonProfitDrawer from './components/profile/nonprofit/NonProfitDrawer';
import ReviewDrawer from './components/reviews/ReviewDrawer';
import CancelSubscriptionDrawer from './components/subscription/CancelDrawer';
import SubscriptionCheckoutDrawer from './components/subscription/CheckoutDrawer';
import DefaultPaymentMethodDrawer from './components/subscription/DefaultPaymentMethodDrawer';
import ReactivateSubscriptionDrawer from './components/subscription/RectivateDrawer';
import PageLoadingProgress from './components/utilities/PageLoadingProgress';
import PromoBanner from './components/utilities/PromoBanner.vue';
import RenameDrawer from './components/utilities/RenameDrawer';
import ZoomImageOverlay from './components/utilities/ZoomImageOverlay';
import customerio from './plugins/customerio';
import googleAnalytics from './plugins/google-analytics';
import helpscout from './plugins/helpscout';
import hubspot from './plugins/hubspot';
import { ANALYTICS_EVENTS, APP_EVENTS, DRAWERS, ROUTES } from './utils/constants';
import customEventHandler from './utils/customEventHandler';
import { loadDesignsRailScript } from './utils/designsRailLoader';
import postMessageHandler from './utils/postMessageHandler';
import setFavicon from './utils/setFavicon';
import { loadSubscriptionCheckoutScript } from './utils/subscriptionCheckoutLoader';

export default {
  name: 'App',

  components: {
    Nav,
    Header,
    UnauthorizedHeader,
    PromoBanner,
    Footer,
    GenericAlert,
    GenericConfirm,
    Clipboard,
    Snackbar,
    AlertBar,
    PageLoadingProgress,
    SubscriptionCheckoutDrawer,
    ReactivateSubscriptionDrawer,
    CancelSubscriptionDrawer,
    DefaultPaymentMethodDrawer,
    AddCardDrawer,
    UpdateCardDrawer,
    ReviewDrawer,
    ProjectProofDrawer,
    ZoomImageOverlay,
    CatalogDrawer,
    EditorDrawer,
    NonProfitDrawer,
    ExemptionCertificatesDrawer,
    AddPeopleDrawer,
    CollectionMultiAppBar,
    ForcePassword,
    RenameDrawer,
    BlankCanvasDrawer,
    BlankCanvasPresetDrawer,
    Project3dDrawer,
    ShareToAccount
  },

  mixins: [helpscout, hubspot, customerio, postMessageHandler, customEventHandler, googleAnalytics],

  data() {
    return {
      authTriggered: false,
      pageCount: 0,
      // Toggled by the <mcs-designs-rail> `rail-visibility` event — only true
      // once the rail reports it has designs to show. Gates the rail's display
      // and the 88px layout gutter (see the `has-designs-rail` styles).
      railVisible: false
    };
  },

  computed: {
    routeKey() {
      return this.$store.state.config.routeKey;
    },

    appLoading() {
      return !this.$store.state.config.appDataLoaded;
    },

    route() {
      return this.$store.state.config.route;
    },

    iframed() {
      return this.$store.state.config.route.meta?.iframe;
    },

    // Gate for loading the designs-rail bundle. `isMCS` depends on the master
    // account, which loads after `authorized` flips true, so we watch this
    // combined condition rather than `authorized` alone (otherwise the loader
    // fires while isMCS is still false and never retries).
    shouldLoadDesignsRail() {
      return this.authorized && this.isMCS && !this.iframed;
    },

    ...mapGetters({
      authorized: 'user/authorized',
      isAdmin: 'user/isAdmin',
      isMCS: 'user/isMCS',
      topDrawer: 'drawers/topDrawer',
      selected: 'admin/collections/selected',
      core_product: 'templates/core_product',
      profile: 'user/profile'
    })
  },

  watch: {
    // Load the AiDesigner bundle (which registers <mcs-designs-rail>) once the
    // user is authorized AND an MCS user — the rail is MCS-only, so we don't
    // fetch the bundle otherwise. Watching the combined condition handles
    // isMCS resolving after authorized. The loader is idempotent.
    shouldLoadDesignsRail: {
      immediate: true,
      handler(val) {
        if (val) this.loadDesignsRail();
      }
    },

    route(val) {
      this.$mixpanel.trackPage();
      if (this.pageCount === 0) {
        if (this.route.query.drawer) {
          this.autoOpenDrawer(this.route.query.drawer);
        }
        this.pageCount++;
      }
    },

    topDrawer(val) {
      if (val?.id) {
        var drawerId = Object.keys(DRAWERS).find((x) => DRAWERS[x] === val.id);
        this.$mixpanel.trackEvent(ANALYTICS_EVENTS.DRAWER_OPENED, null, { id: drawerId });
        this.$nextTick(() => {
          document.documentElement.classList.add('overflow-hidden');
        });
      } else {
        document.documentElement.classList.remove('overflow-hidden');
      }
    }
  },

  async created() {
    console.log('CREATED -> App');

    let skipAuth = false;
    const matched = this.$router.match(window.location.pathname);
    let goToQuery = '';
    if (matched) {
      const route = Object.keys(ROUTES)
        .map((key) => ROUTES[key])
        .find((x) => x.NAME === matched.name);
      if (route && route.ALLOW_ANONYMOUS) {
        skipAuth = true;
      }
      if (
        route &&
        route.NAME === ROUTES.PRINT.NAME &&
        window.location.search?.includes('categoryId')
      ) {
        const categoryId = window.location.search
          .replace('?', '')
          .split('&')
          .filter((x) => x.includes('categoryId'))[0]
          .replace('categoryId=', '');
        goToQuery = `printCategory=${categoryId}`;
      }
    }

    this.$store
      .dispatch('user/authorize')
      .then(() => {
        if (this.$store.getters['user/isAdmin'] && this.$route.query.runas) {
          this.$store.dispatch('user/runas', this.$route.query.runas).then((result) => {
            window.history.pushState(null, '', window.location.pathname);
            if (result.error) {
              this.$root.$alert('Unable to Runas User', result.error, {
                cancelText: '',
                okText: 'Close'
              });
            } else {
              localStorage.setItem('runas', this.$route.query.runas);
              this.$router.go(ROUTES.DASHBOARD.PATH);
            }
          });
        } else {
          this.$store.dispatch('config/getAppData').then(() => {
            this.$mixpanel.peopleSetOnce(
              this.profile.analyticsProfile,
              this.$store.state.user.userId
            );

            this.$loadCustomerIO();
            this.$loadHubspot();
            this.$loadGa();

            this.$mixpanel.initMetaPixel(
              this.$store.state.config.metaPixelId,
              this.$store.state.user.userId,
              this.$store.state.user.username
            );

            this.$store.commit('config/SET_APP_DATA_LOADED', true);
            setFavicon(this.$store.state.config.faviconUrl);
            this.$eventBus.$emit(APP_EVENTS.APP_DATA_READY);

            this.$api.get('data/getLazyAppData').then((response) => {
              this.$store.commit('user/INIT_LAZY_APP_DATA', response.data);
              this.$store.commit('teams/SET_TEAMS', response.data.teams);
              this.$store.commit('lookups/INITIALIZE', response.data.lookupData);
            });

            this.setBrandColor();

            // open the templates on this core product (v1 sign-in with coreProductId sends it)
            if (this.$route.query.coreProductId) {
              const product = this.core_product(parseInt(this.$route.query.coreProductId));
              if (product) {
                setTimeout(() => {
                  this.$store.dispatch('drawers/open', {
                    drawerId: DRAWERS.CATALOG_DRAWER,
                    data: {
                      coreProductId: product.id
                    }
                  });
                }, 1000);
              }
            }
          });
        }
        this.bindPostMessageHandlers();
        this.bindCustomEventHandlers();
      })
      .catch((error) => {
        var trackError = true;
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          if (skipAuth) {
            // anonymous visitor on an ALLOW_ANONYMOUS route — expected 403,
            // don't report to Sentry
            trackError = false;
            this.$store.dispatch('config/getOpenAppData').then(() => {
              this.$store.commit('config/SET_APP_DATA_LOADED', true);
              setFavicon(this.$store.state.config.faviconUrl);
              this.$eventBus.$emit(APP_EVENTS.APP_DATA_READY);
            });
          } else if (error.response.status === 302) {
            trackError = false;
            window.location = error.response.data;
          } else if (error.response.status === 403) {
            trackError = false;
            this.goToLogin(goToQuery);
          } else {
            this.$root.$alert('Authorization Error', error.message, { closeOnly: true });
          }
        } else if (error.request) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          console.log('The request was made but no response was received');
          var msg =
            'Please check your network settings to make sure you have an active internet connection and then refresh this page.';
          if (this.$store.state.config.inDevEnv) {
            msg += '<br/><br/><strong>DEV: This is likely a CORS error</strong>';
          }
          this.$root.$alert('No Response Received', msg, { closeOnly: true });
        } else {
          // Something happened in setting up the request that triggered an Error
          console.log('Unknown error - something happened in setting up the request');
          this.$root.$alert(
            'Unknown error',
            'An unknown error has occurrred. Please try again or contact support',
            { closeOnly: true }
          );
        }
        if (trackError) {
          throw new Error(error);
        }
      });
  },

  mounted() {
    // make our alerts & confirms globally accessible
    this.$nextTick(() => {
      this.$root.$alert = this.$refs.alert.open;
      this.$root.$confirm = this.$refs.confirm.open;
      this.$root.$copy = this.$refs.clipboard.copy;
    });
    this.$eventBus.$once(APP_EVENTS.SNACKBAR_MOUNTED, () => {
      this.$root.$snackbarSuccess = this.$refs.snackbar.openSnackbarSuccess;
      this.$root.$snackbarError = this.$refs.snackbar.openSnackbarError;
    });
    this.$eventBus.$on('refreshDataTableHeader', () => {
      this.$nextTick(() => {
        document
          .getElementsByClassName('v-data-table-header')[0]
          .getElementsByTagName('th')
          .forEach((x) => {
            x.style.width = x.offsetWidth + 30 + 'px';
          });
      });
    });
    if (this.$store.state.config.inDevEnv) {
      window.vm = this;
      // Test-only: expose the subscription embed loader so Playwright
      // integration specs can force the bundle to load without driving a
      // real drawer flow. Production code paths (drawers/open routing) keep
      // the lazy-load behavior — this hook just gives tests a synchronous
      // entry point.
      window.__loadMcsCheckoutSubscription = () =>
        loadSubscriptionCheckoutScript({ inDevEnv: this.$store.state.config.inDevEnv });
      // this.$store.commit('drawers/OPEN_DRAWER', DRAWERS.CANCEL_SUBSCRIPTION_DRAWER);
      window.snackbarSuccess = () => {
        this.$root.$snackbarSuccess('Yay!');
      };
      window.snackbarError = () => {
        this.$root.$snackbarError('Uh oh!');
      };
    }

    // Listen for events from the <mcs-checkout-subscription> embed. After a
    // subscribe / reactivate / cancel / credit purchase inside the drawer,
    // server-side state has changed and the Subscription page / nav / alert
    // bars will show stale info until we refresh. `subscription/getDetail`
    // is the single canonical refresh — it commits subscription.item,
    // cards.items, and user.plan in one call.
    //
    // mcs:credits-changed is intentionally not handled — Dashboard does not
    // display credit balance anywhere, so there's nothing to refresh.
    const refreshSub = () => this.$store.dispatch('subscription/getDetail');
    const snapshot = () =>
      `${this.$store.state.user.plan.isSubscriber}|${!!this.$store.state.subscription.item
        .setToCancel}`;
    window.addEventListener('mcs:subscription-checkout-success', () => {
      this.refreshUntilChanged(refreshSub, snapshot);
    });
    window.addEventListener('mcs:subscription-canceled', () => {
      this.refreshUntilChanged(refreshSub, snapshot);
    });
  },

  methods: {
    // Lazy-load the AiDesigner bundle so the <mcs-designs-rail> custom element
    // upgrades. Idempotent (the loader memoizes its promise); safe to call from
    // both the `authorized` watcher and elsewhere.
    loadDesignsRail() {
      loadDesignsRailScript({ inDevEnv: this.$store.state.config.inDevEnv }).catch((err) =>
        console.error('[mcs-designs-rail] failed to load', err)
      );
    },

    // The rail reports whether it has designs worth showing; mirror that into
    // `railVisible` so the gutter/layout shift only applies when it's visible.
    onRailVisibility(e) {
      this.railVisible = !!e.detail?.visible;
    },

    // Poll the refresh action until the snapshot string changes — handles
    // Stripe webhook lag where the bridge event fires before the DB update
    // lands. ~12s total: 250, 500, 750, 1000, 1500, 2000, 2000, 2000, 2000.
    async refreshUntilChanged(refresh, snapshot) {
      const before = snapshot();
      await refresh();
      if (snapshot() !== before) return;
      const delays = [250, 500, 750, 1000, 1500, 2000, 2000, 2000, 2000];
      for (const delay of delays) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        await refresh();
        if (snapshot() !== before) return;
      }
    },

    autoOpenDrawer(id) {
      if (id === 'nonprofit') {
      }
      var key = Object.keys(DRAWERS).find((key) => DRAWERS[key].toLowerCase() === id);
      if (key) this.$store.commit('drawers/OPEN_DRAWER', DRAWERS[key]);
    }
  }
};
</script>

<style scoped></style>

<style>
/* <mcs-designs-rail> is a custom element, which defaults to display:inline and
   therefore has no box — it must be given a display + position by the host page
   (the AiDesigner shadow CSS intentionally leaves :host display to the host).
   Pin it as a full-height strip on desktop and a bottom bar on mobile; the
   element's own shadow CSS sizes the rail (88px wide / bottom bar) within it. */
/* Hidden by default — the rail stays in the DOM (to fetch designs) but is only
   revealed once it reports it has designs to show (App adds `has-designs-rail`).
   While hidden it occupies no space and the Vuetify layout is left untouched. */
mcs-designs-rail {
  display: none;
}

.has-designs-rail mcs-designs-rail {
  display: block;
  position: fixed;
  inset: 0 auto 0 0; /* top:0 right:auto bottom:0 left:0 */
  width: 88px;
  z-index: 7;
}

/* Desktop/tablet: the rail is a far-left gutter, so shift the whole Vuetify
   layout (nav drawer, app bar, main content) right by the rail width.
   - Vuetify 2 has no `--left` modifier (only `--right`), so a left drawer is
     `.v-navigation-drawer--fixed` without `--right`. It sets its position via
     `transform: translateX()` + CSS `left:0`, so overriding `left` shifts it.
   - The app bar sets `left`/`right` inline; on a fixed element with both set,
     `margin-left` both shifts it right and shrinks its width, so its right edge
     stays at the viewport edge. Same `margin-left` trick shifts `v-main`.
   This shifts by a fixed 88px regardless of the drawer's width. */
@media (min-width: 601px) {
  .has-designs-rail .v-application .v-navigation-drawer--fixed:not(.v-navigation-drawer--right) {
    left: 88px !important;
  }
  .has-designs-rail .v-application .v-app-bar--fixed {
    margin-left: 88px !important;
  }
  .has-designs-rail .v-application .v-main {
    margin-left: 88px !important;
  }
}

/* Mobile: the rail becomes a bottom bar; leave the Vuetify layout untouched.
   Drop it below the nav drawer (z-index 6) and its scrim overlay (z-index 5)
   so an open nav — and its dimming backdrop — render over the rail. */
@media (max-width: 600px) {
  .has-designs-rail mcs-designs-rail {
    inset: auto 0 0 0; /* pin to the bottom, full width */
    width: 100%;
    z-index: 4;
  }
}
</style>
